import { TDocumentDefinitions } from "pdfmake/interfaces";

// Config
import CONFIG from "config/config";

// Common
import { getCompanyFont } from "../common/CompanyLogoForm";
import { dateToStringBE } from "react-lib/utils/dateUtils";
import {
  HeaderNonLogoWithPatientDetailImage,
  HeaderNonLogoWithPatientProps,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

// Utils
import moment from "moment";

// Serializer
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

export type FormMedicalNoteOrderSummaryProps = {
  detail: HeaderNonLogoWithPatientProps["detail"];
  encounterData: EncounterSerializer;
  orderSummaryData: Partial<{
    continue: Record<string, any>[];
    oneDay: Record<string, any>[];
  }>;
  patientData: PatientSerializer;
  userFullName: string;
};

/**============================================
 **                   CONST
 *=============================================**/
const FONT_SIZE = 14;

const FORM_NAME = "FormMedicalNoteOrderSummary";

/**============================================
 **                  UTILS
 *=============================================**/

const hasHtmlTag = (text: string | null | undefined): boolean => {
  if (!text) return false;

  return /<[^>]+>/.test(text);
};

const htmlToText = (html: string | null | undefined): string => {
  if (!html) return "";

  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim();
};

const safeText = (input: any) => htmlToText(input || "") || "";

// const safeField = (input: any) => input || "";

/**============================================
 **                   FORM
 *=============================================**/
const FormMedicalNoteOrderSummary = async (
  props: FormMedicalNoteOrderSummaryProps
): Promise<TDocumentDefinitions> => {

  const currentDate = dateToStringBE(moment());

  const headerForm = await HeaderNonLogoWithPatientDetailImage({
    detail: props.detail,
    font: getCompanyFont(),
    form: FORM_NAME,
    formName: "Doctor Order Summary",
    pageMargins: [15, 160, 15, 25],
    // content: []
  });

  const { font, fontSizes, lineHeights, images, styles } = headerForm;

  const mergedOrderData = () => {
    const oneDay = (props.orderSummaryData.oneDay || []).filter(
      (item: any) => item.summary !== "ไม่มีข้อมูล"
    );
    const cont = (props.orderSummaryData.continue || []).filter(
      (item: any) => item.summary !== "ไม่มีข้อมูล"
    );

    const maxLength = Math.max(oneDay.length, cont.length);
    const result = [];

    for (let i = 0; i < maxLength; i++) {
      const one = oneDay[i] || {};
      const con = cont[i] || {};

      result.push([
        safeText(one.order_time),
        safeText(one.summary),
        {
          text: safeText(one.nurse_order),
          alignment: hasHtmlTag(one.nurse_order) ? "left" : "center",
        },
        safeText(con.order_time),
        safeText(con.summary),
        {
          text: safeText(con.nurse_order),
          alignment: hasHtmlTag(con.nurse_order) ? "left" : "center",
        },
      ]);
    }

    return result;
  };

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[FONT_SIZE],
      lineHeight: lineHeights[1],
    },
    pageSize: "A4",
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: ["12%", "26%", "12%", "12%", "26%", "12%"],
          body: [
            [
              { text: "Order by Date/Time", bold: true, alignment: "center" },
              { text: "Oneday Order", bold: true, alignment: "center" },
              { text: "Received by Date/Time", bold: true, alignment: "center" },
              { text: "Order by Date/Time", bold: true, alignment: "center" },
              { text: "Continuetion Order", bold: true, alignment: "center" },
              { text: "Received by Date/Time", bold: true, alignment: "center" },
            ],
            ...mergedOrderData(),
          ],
        },
      },
    ],
    footer: (currentPage: number, pageCount: number) => {
      return {
        table: {
          widths: ["100%"],
          body: [
            [
              {
                columns: [
                  {
                    text: `วันเวลาที่พิมพ์เอกสาร ${currentDate} โดย ${props.userFullName || "-"}`,
                    alignment: `left`,
                    fontSize: fontSizes[12],
                  },
                  {
                    text: `หน้า ${currentPage.toString()} จาก ${pageCount}`,
                    alignment: `right`,
                    fontSize: fontSizes[12],
                    bold: true,
                  },
                ],
                border: [false, true, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineStyle: (i: any, node: any) => {
            return { dash: { length: 2, space: 2 } };
          },
          paddingLeft: () => 15,
          paddingRight: () => 15,
        },
      };
    },
  };
};

export default FormMedicalNoteOrderSummary;
